import { useState, useMemo, useCallback, useEffect } from 'react';
import { useAppSelector } from '../../../../contexts/hooks';
import CustomStore from 'devextreme/data/custom_store';

interface UseFilterStudiesDropDownReturn {
  studiesDropDownValue: number[];
  isStudiesDropDownOpened: boolean;
  studiesSearchValue: string;
  studiesNavigationIndex: number;
  studiesDataSource: any;
  studiesDisplayExpr: (item: any) => string;
  setIsStudiesDropDownOpened: (opened: boolean) => void;
  onStudiesSelectionChanged: (e: any) => void;
  onStudiesRowDblClick: (e: any) => void;
  onStudiesSearchValueChanged: (e: any) => void;
  onStudiesKeyDown: (e: any) => void;
  onStudiesDropDownOpened: (e: any) => void;
  syncStudiesSelection: (e: any) => void;
  resetStudiesDropDown: () => void;
}

export const useFilterStudiesDropDown = (onSelectionChange?: (selectedIds: number[]) => void): UseFilterStudiesDropDownReturn => {
  const estudies = useAppSelector((x) => x.configuration.Data?.Estudios || []);

  // Estados
  const [studiesDropDownValue, setStudiesDropDownValue] = useState<number[]>([]);
  const [isStudiesDropDownOpened, setIsStudiesDropDownOpened] = useState(false);
  const [studiesSearchValue, setStudiesSearchValue] = useState('');
  const [filteredStudies, setFilteredStudies] = useState(estudies);
  const [studiesNavigationIndex, setStudiesNavigationIndex] = useState(0);

  // DataSource
  const studiesDataSource = useMemo(() => {
    return new CustomStore({
      key: 'Es_Id',
      loadMode: 'raw',
      load: () => Promise.resolve(filteredStudies),
    });
  }, [filteredStudies]);

  // Display function
  const studiesDisplayExpr = useCallback((item: any) => {
    return item ? `${item.Es_Nombre} - ${item.Es_Codigo}` : '';
  }, []);

  // Filtrar estudios
  const filterStudies = useCallback(
    (searchText: string) => {
      if (!searchText.trim()) {
        setFilteredStudies(estudies);
        setStudiesNavigationIndex(0);
        return;
      }

      const filtered = estudies.filter(
        (study) =>
          study.Es_Nombre.toLowerCase().includes(searchText.toLowerCase()) ||
          study.Es_Codigo.toLowerCase().includes(searchText.toLowerCase()) ||
          study.SysA_AreasAnalisis?.Aa_Nombre?.toLowerCase().includes(searchText.toLowerCase())
      );

      setFilteredStudies(filtered);
      setStudiesNavigationIndex(0);

      if (filtered.length > 0) {
        setStudiesDropDownValue([filtered[0].Es_Id]);
      } else {
        setStudiesDropDownValue([]);
      }
    },
    [estudies]
  );

  // Navegación
  const navigateStudiesUp = useCallback(() => {
    if (filteredStudies.length === 0) return;
    const newIndex = studiesNavigationIndex > 0 ? studiesNavigationIndex - 1 : filteredStudies.length - 1;
    setStudiesNavigationIndex(newIndex);
    setStudiesDropDownValue([filteredStudies[newIndex].Es_Id]);
  }, [filteredStudies, studiesNavigationIndex]);

  const navigateStudiesDown = useCallback(() => {
    if (filteredStudies.length === 0) return;
    const newIndex = studiesNavigationIndex < filteredStudies.length - 1 ? studiesNavigationIndex + 1 : 0;
    setStudiesNavigationIndex(newIndex);
    setStudiesDropDownValue([filteredStudies[newIndex].Es_Id]);
  }, [filteredStudies, studiesNavigationIndex]);

  // Seleccionar estudio para filtro
  const selectStudy = useCallback(
    (studyId: number) => {
      setStudiesDropDownValue([studyId]);
      setIsStudiesDropDownOpened(false);
      
      // Notificar al componente padre
      if (onSelectionChange) {
        onSelectionChange([studyId]);
      }
    },
    [onSelectionChange]
  );

  // Limpiar y resetear
  const resetStudiesDropDown = useCallback(() => {
    setStudiesDropDownValue([]);
    setStudiesSearchValue('');
    setFilteredStudies(estudies);
    setStudiesNavigationIndex(0);
    
    // Notificar al componente padre
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  }, [estudies, onSelectionChange]);

  // Handlers
  const onStudiesSelectionChanged = useCallback((e: any) => {
    const selectedKeys = e.selectedRowKeys;
    if (selectedKeys && selectedKeys.length > 0) {
      setStudiesDropDownValue(selectedKeys);
    }
  }, []);

  const onStudiesRowDblClick = useCallback(
    (e: any) => {
      selectStudy(e.data.Es_Id);
    },
    [selectStudy]
  );

  const onStudiesSearchValueChanged = useCallback(
    (e: any) => {
      const value = e.value;
      setStudiesSearchValue(value);
      filterStudies(value);
    },
    [filterStudies]
  );

  const onStudiesKeyDown = useCallback(
    (e: any) => {
      switch (e.event.key) {
        case 'Enter':
          if (studiesDropDownValue.length > 0) {
            selectStudy(studiesDropDownValue[0]);
          }
          break;
        case 'ArrowUp':
          e.event.preventDefault();
          navigateStudiesUp();
          break;
        case 'ArrowDown':
          e.event.preventDefault();
          navigateStudiesDown();
          break;
        case 'Escape':
          setIsStudiesDropDownOpened(false);
          break;
      }
    },
    [studiesDropDownValue, selectStudy, navigateStudiesUp, navigateStudiesDown]
  );

  const onStudiesDropDownOpened = useCallback((e: any) => {
    if (e.name === 'opened') {
      setIsStudiesDropDownOpened(e.value);
      
      if (e.value) {
        // Focus en el input de búsqueda cuando se abre
        setTimeout(() => {
          const searchInput = document.querySelector('#filter-studies-search-input') as HTMLInputElement;
          searchInput?.focus();
        }, 100);
      }
    }
  }, []);

  const syncStudiesSelection = useCallback(
    (e: any) => {
      // Manejar cuando se limpia el valor
      if (!e.value || e.value === null || e.value === undefined) {
        resetStudiesDropDown();
      }
    },
    [resetStudiesDropDown]
  );

  // Efecto para inicializar los datos filtrados
  useEffect(() => {
    setFilteredStudies(estudies);
  }, [estudies]);

  return {
    studiesDropDownValue,
    isStudiesDropDownOpened,
    studiesSearchValue,
    studiesNavigationIndex,
    studiesDataSource,
    studiesDisplayExpr,
    setIsStudiesDropDownOpened,
    onStudiesSelectionChanged,
    onStudiesRowDblClick,
    onStudiesSearchValueChanged,
    onStudiesKeyDown,
    onStudiesDropDownOpened,
    syncStudiesSelection,
    resetStudiesDropDown,
  };
};
